import React, { useCallback, useMemo, useRef } from 'react';
import moment from 'moment/moment';
import Row from '../../../core/layout/Row';
import Column from '../../../core/layout/Column';
import FetchSelectV2 from '../../../core/form/FetchSelectV2';
import get from 'lodash/get';
import TextInput from '../../../core/form/TextInput';
import PropTypes from 'prop-types';
import MaskInput from '../../../core/form/MaskInput';
import DateInput from '../../../core/form/DateInput';
import Select from '../../../core/form/Select';
import Separator from '../../../core/layout/Separator';
import Card from '../../../core/layout/Card';
import Button from '../../../core/form/Button';
import { browserHistory } from 'react-router';
import FormButtons from '../../../core/form/FormButtons';
import LocalFileInput from '../../../core/form/LocalFileInput';
import { BRAZIL, DOCUMENT_NUMBER_MASK_OPTIONS } from '../../../core/consts';
import ActivateContractProductsListContainer from './ConfirmRenewalContractProductsListContainer';
import MonthInput from '../../../core/form/MonthInput';

const ConfirmRenewalContractForm = (props) => {
  const maskInputRef = useRef();

  const handleDocumentNumberChange = useCallback(
    (value) => {
      if (value.length > 11) {
        maskInputRef.current.updateMaskOptions(DOCUMENT_NUMBER_MASK_OPTIONS.cnpj);
      } else {
        maskInputRef.current.updateMaskOptions(DOCUMENT_NUMBER_MASK_OPTIONS.cpf);
      }
      props.onChange('documentNumber', value);
    },
    [props]
  );

  const allowedDueDays = useMemo(() => {
    const dueDays = [];
    const initialContractDate = get(props.values, 'startsAt', null);
    if (props.values.type === 'UPFRONT' && initialContractDate) {
      const dayOfContractStart = moment(initialContractDate).date();
      const nextInvoiceGenerationDate = moment().set('date', dayOfContractStart).add(1, 'months');
      const minDueDay = moment().set('date', dayOfContractStart).add(1, 'day').date();
      const maxDueDay = nextInvoiceGenerationDate.clone().subtract(19, 'days').date();

      if (maxDueDay < minDueDay) {
        for (let i = minDueDay; i <= 31; i += 1) {
          dueDays.push(i);
        }
        for (let i = 1; i <= maxDueDay; i += 1) {
          dueDays.push(i);
        }
      } else {
        for (let i = minDueDay; i <= maxDueDay; i += 1) {
          dueDays.push(i);
        }
      }
      return dueDays;
    }
    for (let i = 1; i <= 31; i += 1) {
      dueDays.push(i);
    }
    return dueDays;
  }, [props.values]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        props.onSubmit();
      }}
    >
      <Card
        title="Activate Contract"
        actions={<Button icon="arrow-left" label="Back" type="default" onClick={() => browserHistory.goBack()} />}
      >
        <Row>
          <Column size={3}>
            <FetchSelectV2
              required
              showSearch
              url="countries"
              apiVersion="v3"
              disabled={props.submitting || props.loading || props.disabled}
              label="Country"
              value={get(props.values, 'country', '')}
              onChange={(country) => props.onChange('country', country)}
              errorText={get(props.errors, 'country', '')}
              resultTransformer={{
                text: 'name',
                value: 'id',
              }}
            />
          </Column>
          <Column size={3}>
            <FetchSelectV2
              required
              showSearch
              url="states"
              apiVersion="v3"
              params={{
                ...(get(props.values, 'country', false) && {
                  country: get(props.values, 'country', ''),
                }),
              }}
              disabled={props.submitting || props.loading || !get(props.values, 'country', false) || props.disabled}
              label="State"
              value={get(props.values, 'state', '')}
              onChange={(state) => props.onChange('state', state)}
              errorText={get(props.errors, 'state', '')}
              resultTransformer={{
                text: 'name',
                value: 'id',
              }}
            />
          </Column>
          <Column size={6}>
            <TextInput
              floatingLabel
              fullWidth
              disabled={props.submitting || props.loading || props.disabled}
              label="City"
              value={get(props.values, 'city', '')}
              onChange={(value) => props.onChange('city', value)}
              errorText={get(props.errors, 'city', '')}
            />
          </Column>
        </Row>
        <Row>
          <Column size={4}>
            {BRAZIL.includes(props.values.country) ? ( // Brazil
              <MaskInput
                disabled={props.submitting || props.loading || props.disabled}
                label="CPF/CNPJ"
                value={get(props.values, 'documentNumber', '')}
                onChange={handleDocumentNumberChange}
                errorText={get(props.errors, 'documentNumber', '')}
                delimiters={['.', '.', '/', '-']}
                blocks={[2, 3, 3, 4, 2]}
                numericOnly
                required
                ref={maskInputRef}
              />
            ) : (
              <TextInput
                required
                disabled={props.submitting || props.loading || props.disabled}
                label="Document Number"
                value={get(props.values, 'documentNumber', '')}
                onChange={(value) => props.onChange('documentNumber', value)}
                errorText={get(props.errors, 'documentNumber', '')}
              />
            )}
          </Column>
          <Column size={8}>
            <TextInput
              disabled={props.submitting || props.loading || props.disabled}
              required
              label="Social Reason"
              value={get(props.values, 'socialReason', '')}
              onChange={(value) => props.onChange('socialReason', value)}
              errorText={get(props.errors, 'socialReason', '')}
            />
          </Column>
        </Row>
        <Row>
          <Column size={4}>
            <TextInput
              floatingLabel
              required
              fullWidth
              disabled={props.submitting || props.loading || props.disabled}
              label="Address"
              value={get(props.values, 'address', '')}
              onChange={(value) => props.onChange('address', value)}
              errorText={get(props.errors, 'address', '')}
            />
          </Column>
          <Column size={4}>
            <TextInput
              floatingLabel
              required
              fullWidth
              disabled={props.submitting || props.loading || props.disabled}
              label="District"
              value={get(props.values, 'district', '')}
              onChange={(value) => props.onChange('district', value)}
              errorText={get(props.errors, 'district', '')}
            />
          </Column>
          <Column size={4}>
            <MaskInput
              disabled={props.submitting || props.loading || props.disabled}
              required
              label="Number"
              value={get(props.values, 'number', null)}
              onChange={(value) => props.onChange('number', value)}
              errorText={get(props.errors, 'number', '')}
              blocks={[9]}
              numericOnly
            />
          </Column>
        </Row>
        <Row>
          <Column size={4}>
            <TextInput
              floatingLabel
              required
              fullWidth
              disabled={props.submitting || props.loading || props.disabled}
              label="Zip code"
              value={get(props.values, 'zipCode', '')}
              onChange={(value) => props.onChange('zipCode', value)}
              errorText={get(props.errors, 'zipCode', '')}
            />
          </Column>
          <Column size={4}>
            <Select
              mode="tags"
              required
              disabled={props.submitting || props.loading || props.disabled}
              label="Emails"
              value={get(props.values, 'emails', [])}
              onChange={(value) =>
                props.onChange(
                  'emails',
                  value.map((i) => i.toLowerCase())
                )
              }
              errorText={get(props.errors, 'emails', null)}
            />
          </Column>
          <Column size={4}>
            <MaskInput
              floatingLabel
              fullWidth
              disabled={props.submitting || props.loading || props.disabled}
              label="Phone"
              value={get(props.values, 'phone', '')}
              onChange={(value) => props.onChange('phone', value)}
              errorText={get(props.errors, 'phone', '')}
              maskType="phone"
            />
          </Column>
        </Row>
        <Row>
          <Column size={2}>
            <Select
              mode="multiple"
              required
              disabled={props.submitting || props.loading || props.disabled}
              label="Allowed Payment Methods"
              value={get(props.values, 'allowedPaymentMethods', [])}
              onChange={(value) => props.onChange('allowedPaymentMethods', value)}
              errorText={get(props.errors, 'allowedPaymentMethods', '')}
              options={[
                { value: 'CREDIT_CARD', label: 'Credit Card' },
                { value: 'BANK_SLIP', label: 'Bank Slip' },
                { value: 'PIX', label: 'Pix' },
              ]}
            />
          </Column>
          <Column size={1}>
            <TextInput
              floatingLabel
              fullWidth
              required
              disabled
              label="Hubspot Deal Id"
              value={get(props.values, 'hubspotDealId', '')}
              onChange={(value) => props.onChange('hubspotDealId', value)}
              errorText={get(props.errors, 'hubspotDealId', '')}
            />
          </Column>
          <Column size={1}>
            <Select
              disabled
              label="Type"
              value={get(props.values, 'type', null)}
              onChange={(value) => props.onChange('type', value)}
              errorText={get(props.errors, 'type', '')}
              options={[
                { value: 'UPFRONT', label: 'Upfront' },
                { value: 'POST_PAYMENT', label: 'Post Payment' },
              ]}
            />
          </Column>
          <Column size={2}>
            {get(props.values, 'type', null) === 'POST_PAYMENT' ? (
              <MonthInput
                disabled={props.submitting || props.loading || props.disabled}
                label="Contract starts in"
                value={get(props.values, 'startsAt', null)}
                onChange={(value) => props.onChange('startsAt', value)}
                errorText={get(props.errors, 'startsAt', '')}
              />
            ) : (
              <DateInput
                disabled={props.submitting || props.loading || props.disabled}
                label="Contract starts in"
                value={get(props.values, 'startsAt', null)}
                onChange={(value) => props.onChange('startsAt', value)}
                errorText={get(props.errors, 'startsAt', '')}
              />
            )}
          </Column>
          <Column size={2}>
            {get(props.values, 'type', null) === 'POST_PAYMENT' ? (
              <MonthInput
                disabled={props.submitting || props.loading || props.disabled}
                label="Contracts ends in"
                value={get(props.values, 'endsAt', null)}
                onChange={(value) => props.onChange('endsAt', value.endOf('month'))}
              />
            ) : (
              <DateInput
                disabled={props.submitting || props.loading || props.disabled}
                label="Contracts ends in"
                value={get(props.values, 'endsAt', null)}
                onChange={(value) => props.onChange('endsAt', value)}
              />
            )}
          </Column>
          <Column size={1}>
            <Select
              label="Due day"
              required={true}
              disabled={props.submitting || props.loading || props.disabled}
              value={get(props.values, 'dueDay', '')}
              onChange={(value) => props.onChange('dueDay', value)}
              errorText={get(props.errors, 'dueDay', null)}
              options={allowedDueDays.map((n) => ({
                label: n,
                value: n,
              }))}
            />
          </Column>
          <Column size={3}>
            <LocalFileInput
              value={get(props.values, 'contractFile', null) || get(props.values, 'contractUrl', null)}
              onChange={(value) => props.onChange('contractFile', value)}
              label="Upload the contract"
              accept="pdf"
              disabled={props.submitting || props.loading || props.disabled}
            />
          </Column>
        </Row>
        <Row>
          <Column size={3}>
            {!get(props.values, 'isDistributor', false) && (
              <FetchSelectV2
                showSearch
                url="companies"
                apiVersion="v3"
                disabled
                label="Company"
                value={get(props.values, 'company', null)}
                onChange={(value) => props.onChange('company', value)}
                placeholder="Create a new company"
                resultFilter={(company) => company.id === get(props.values, 'company', null) || !company.contract}
                resultTransformer={{
                  text: 'name',
                  value: 'id',
                }}
              />
            )}
            {get(props.values, 'isDistributor', false) && (
              <FetchSelectV2
                showSearch
                required
                url="distributors"
                apiVersion="v3"
                disabled
                label="Distributor"
                value={get(props.values, 'distributor')}
                onChange={(value) => props.onChange('distributor', value)}
                resultTransformer={{
                  text: 'name',
                  value: 'id',
                }}
                resultFilter={(distributor) =>
                  distributor.contract === get(props.values, 'id', null) || !distributor.contract
                }
                errorText={get(props.errors, 'distributor', null)}
              />
            )}
          </Column>
          {!get(props.values, 'isDistributor', false) && !get(props.values, 'company', null) && (
            <Column size={3}>
              <TextInput
                disabled={props.submitting || props.loading || props.disabled}
                label="Company name"
                required
                value={get(props.values, 'companyName', '')}
                onChange={(value) => props.onChange('companyName', value)}
                errorText={get(props.errors, 'companyName', null)}
              />
            </Column>
          )}
          {!get(props.values, 'isDistributor', false) && !get(props.values, 'company', null) && (
            <Column size={3}>
              <FetchSelectV2
                showSearch
                required
                url="/segments"
                apiVersion="v3"
                disabled={props.submitting || props.loading || props.disabled}
                label="Segment"
                value={get(props.values, 'companySegment', '')}
                onChange={(companySegment) => props.onChange('companySegment', companySegment)}
                errorText={get(props.errors, 'companySegment', '')}
                resultTransformer={{
                  text: 'name',
                  value: 'id',
                }}
              />
            </Column>
          )}
        </Row>
      </Card>
      <Separator />
      <Card
        title="Contract's Products"
        actions={
          <Button
            label="Add Product"
            onClick={props.onAddProduct}
            disabled={props.submitting || props.loading || props.disabled}
          />
        }
      >
        <ActivateContractProductsListContainer />
      </Card>
      <Separator />
      <FormButtons
        confirmLabel="Activate Contract"
        isDisabled={props.submitting || props.loading || props.disabled}
        isSubmitting={props.submitting}
        onReset={props.onReset}
      />
    </form>
  );
};

ConfirmRenewalContractForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  values: PropTypes.object,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
  submitting: PropTypes.bool,
  loading: PropTypes.bool,
  productsService: PropTypes.object,
  onAddProduct: PropTypes.func.isRequired,
};

ConfirmRenewalContractForm.defaultProps = {
  onReset: () => false,
  values: {},
  errors: {},
  submitting: false,
  loading: false,
};

export default ConfirmRenewalContractForm;
